import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import "./ViewNoteModal.css";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ViewNoteModal = ({
  noteId,
  jobId,
  taskId,
  onClose,
  currentTheme,
  onViewAttachments,
  userid,
  scrollToNoteId,
}) => {
  const [currentNote, setCurrentNote] = useState(null);
  const [relatedNotes, setRelatedNotes] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentLightboxImages, setCurrentLightboxImages] = useState([]);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [showJobInfo, setShowJobInfo] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [maxNotes, setMaxNotes] = useState("All");
  const [currentTaskTitle, setCurrentTaskTitle] = useState("");

  const noteRefs = useRef({});
  const chatContainerRef = useRef(null);
  const lightboxRef = useRef(null);

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // ---------- Helper: Normalize a note object ----------
  const normalizeNote = (n) => ({
    ...n,
    userName: n.UserName || n.userName,
    documentCount: n.DocumentCount || n.documentCount || 0,
    date: n.date || "",
    timeStamp: n.timeStamp || n.noteDate || "",
    inlineImageCount: n.inlineImageCount || 0,
    inlineImageUrls: n.inlineImageUrls || [],
    replyCount: n.replyCount || n.repliesCount || 0,
    replyIds: n.replyIds || [],
    siteNoteFriendlyId: n.siteNoteFriendlyId || "",
    replayNotes: (n.replayNotes || n.replies || n.replyNotes || []).map(
      (reply) => {
        if (
          typeof reply === "object" &&
          reply !== null &&
          reply.note !== undefined
        )
          return reply;
        if (typeof reply === "string")
          return {
            note: reply,
            userName: "Unknown",
            timeStamp: "",
            isReply: true,
          };
        return reply;
      },
    ),
    isReply: false,
    taskId: n.taskId ?? null,
    taskTitle: n.taskTitle || "",
  });

  // ---------- Fetch full reply objects by IDs ----------
  const fetchFullReplies = async (replyIds, originalRawReplies = []) => {
    if (!replyIds || replyIds.length === 0) return [];
    try {
      const replies = await Promise.all(
        replyIds.map(async (id, index) => {
          try {
            const res = await fetch(`${apiUrl}/SiteNote/GetSiteNoteById/${id}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const replyNote = data.siteNote || data;
            return {
              id: replyNote.id || id,
              note: replyNote.note || originalRawReplies[index] || "",
              userName: replyNote.userName || replyNote.UserName || "Unknown",
              timeStamp:
                replyNote.timeStamp ||
                replyNote.noteDate ||
                replyNote.date ||
                "",
              date: replyNote.date || "",
              inlineImageUrls: replyNote.inlineImageUrls || [],
              inlineImageCount: replyNote.inlineImageCount || 0,
              documentCount: replyNote.documentCount || 0,
              isReply: true,
              taskId: replyNote.taskId,
              siteNoteFriendlyId: replyNote.siteNoteFriendlyId || "",
            };
          } catch (err) {
            console.error(`Failed to fetch reply ${id}:`, err);
            const fallbackText =
              originalRawReplies[index] || "[Failed to load reply]";
            return {
              id: id,
              note: fallbackText,
              userName: "Unknown",
              timeStamp: "",
              date: "",
              inlineImageUrls: [],
              inlineImageCount: 0,
              documentCount: 0,
              isReply: true,
              taskId: null,
              siteNoteFriendlyId: "",
            };
          }
        }),
      );
      return replies
        .filter((r) => r)
        .sort((a, b) => new Date(a.timeStamp) - new Date(b.timeStamp));
    } catch (error) {
      console.error("Error fetching replies batch:", error);
      return [];
    }
  };

  // ---------- Enrich a note with full replies ----------
  const enrichNoteWithReplies = async (note) => {
    const hasValidReplies =
      note.replayNotes &&
      note.replayNotes.length > 0 &&
      note.replayNotes[0]?.userName &&
      note.replayNotes[0]?.userName !== "Unknown" &&
      note.replayNotes[0]?.timeStamp;
    if (hasValidReplies) return note;

    if (!note.replyIds || note.replyIds.length === 0 || note.replyCount === 0) {
      return note;
    }

    const originalRawReplies = (note.replayNotes || []).map((r) =>
      typeof r === "string" ? r : r.note || "",
    );

    const fullReplies = await fetchFullReplies(
      note.replyIds,
      originalRawReplies,
    );
    if (fullReplies.length > 0) {
      return { ...note, replayNotes: fullReplies };
    }
    return note;
  };

  // ---------- Fetch all notes for the task ----------
  const fetchNotes = async () => {
    if (!taskId && !noteId) { onClose(); return; }
    setLoading(true);
    try {
      let resolvedTaskId = taskId ? Number(taskId) : null;
      let resolvedJobId = jobId;

      // If no taskId prop, get it from the note first
      if (!resolvedTaskId && noteId) {
        const noteRes = await fetch(`${apiUrl}/SiteNote/GetSiteNoteById/${noteId}`);
        if (!noteRes.ok) throw new Error("Failed to fetch note");
        const noteData = await noteRes.json();
        const siteNote = noteData.siteNote || noteData;
        resolvedTaskId = siteNote.taskId ? Number(siteNote.taskId) : null;
        resolvedJobId = jobId || siteNote.jobId;
      }

      if (!resolvedTaskId) {
        setRelatedNotes([]);
        setLoading(false);
        return;
      }

      // Fetch job details for the header
      if (resolvedJobId) {
        const jobRes = await fetch(`${apiUrl}/Job/GetJobById/${resolvedJobId}`);
        if (jobRes.ok) {
          const jobData = await jobRes.json();
          setJobDetails(jobData.job || jobData);
        }
      }

      // Fetch all notes for this task
      const url = `${apiUrl}/SiteNote/GetSiteNotesByTaskId?pageNumber=1&pageSize=1000&taskId=${resolvedTaskId}&userId=${userid}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch task notes");
      const data = await res.json();

      let notes = (data.siteNotes || [])
        .map(normalizeNote)
        .sort((a, b) => {
          const da = new Date(a.date);
          const db = new Date(b.date);
          if (da.getTime() !== db.getTime()) return da - db;
          return a.id - b.id;
        });

      // Build id→note map and wire replyIds to actual note objects from the list
      const noteMap = {};
      notes.forEach((n) => { noteMap[n.id] = n; });
      notes = notes.map((n) => {
        if (!n.replyIds || n.replyIds.length === 0) return n;
        const resolved = n.replyIds.map((id) => noteMap[id]).filter(Boolean);
        return resolved.length > 0 ? { ...n, replayNotes: resolved } : n;
      });

      // Remove reply notes from the top-level list — they appear inline under their parent
      const replyIdSet = new Set(notes.flatMap((n) => n.replyIds || []));
      notes = notes.filter((n) => !replyIdSet.has(n.id));

      // Get jobId from first note if not already resolved (for header)
      if (!resolvedJobId && notes.length > 0 && notes[0].jobId) {
        const jobRes = await fetch(`${apiUrl}/Job/GetJobById/${notes[0].jobId}`);
        if (jobRes.ok) {
          const jobData = await jobRes.json();
          setJobDetails(jobData.job || jobData);
        }
      }

      setRelatedNotes(notes);
      setCurrentNote(noteId ? (notes.find((n) => n.id === noteId) || null) : null);

      if (notes.length > 0 && notes[0].taskTitle) {
        setCurrentTaskTitle(notes[0].taskTitle);
      }
    } catch (err) {
      console.error("ViewNoteModal error:", err);
      toast.error("Failed to load notes");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // ---------- Decide which fetch to use based on props ----------
  useEffect(() => {
    fetchNotes();
  }, [taskId, noteId, jobId, userid]);

  // ---------- Emoji handling (unchanged) ----------
  const emojiPatterns = [
    /[\u{1F600}-\u{1F64F}]/gu,
    /[\u{1F300}-\u{1F5FF}]/gu,
    /[\u{1F680}-\u{1F6FF}]/gu,
    /[\u{1F700}-\u{1F77F}]/gu,
    /[\u{1F780}-\u{1F7FF}]/gu,
    /[\u{1F800}-\u{1F8FF}]/gu,
    /[\u{1F900}-\u{1F9FF}]/gu,
    /[\u{1FA00}-\u{1FA6F}]/gu,
    /[\u{1FA70}-\u{1FAFF}]/gu,
    /[\u{2600}-\u{26FF}]/gu,
    /[\u{2700}-\u{27BF}]/gu,
    /[\u{E000}-\u{F8FF}]/gu,
    /[\u{FE00}-\u{FE0F}]/gu,
    /[\u{1F1E6}-\u{1F1FF}]/gu,
  ];

  const fixGarbledEmojis = (text) => {
    if (!text) return text;
    let fixedText = text;
    const garbledToEmojiMap = {
      "Ø>ÞàØ<ßI": "😀",
      "Ø>ÞàØ<ßJ": "😃",
      "Ø>ÞàØ<ßK": "😄",
      "Ø>ÞàØ<ßL": "😁",
      "Ø>ÞàØ<ßM": "😆",
      "Ø>ÞàØ<ßN": "😅",
      "Ø>ÞàØ<ßO": "😂",
      "Ø>ÞàØ<ßP": "🤣",
      "Ø>ÞàØ<ßQ": "😊",
      "Ø>ÞàØ<ßR": "😇",
      "Ø>ÞàØ<ßS": "🙂",
      "Ø>ÞàØ<ßT": "🙃",
      "Ø>ÞàØ<ßU": "😉",
      "Ø>ÞàØ<ßV": "😌",
      "Ø>ÞàØ<ßW": "😍",
      "Ø>ÞàØ<ßX": "😘",
      "Ø>ÞàØ<ßY": "😗",
      "Ø>ÞàØ<ßZ": "😙",
    };
    Object.keys(garbledToEmojiMap).forEach((garbled) => {
      const regex = new RegExp(
        garbled.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g",
      );
      fixedText = fixedText.replace(regex, garbledToEmojiMap[garbled]);
    });
    return fixedText;
  };

  const createEmojiImage = (emoji, fontSize = 12) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const size = Math.max(fontSize * 1.2, 20);
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `bold ${fontSize}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", "Android Emoji", "EmojiOne Color", "Twemoji Mozilla", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "black";
      ctx.fillText(emoji, canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error creating emoji image:", error);
      return null;
    }
  };

  const splitTextIntoChunks = (text) => {
    if (!text) return [];
    const fixedText = fixGarbledEmojis(text);
    const chunks = [];
    let currentChunk = "";
    let inEmojiChunk = false;
    for (let i = 0; i < fixedText.length; i++) {
      const char = fixedText[i];
      const nextChar = fixedText[i + 1];
      const charCode = char.charCodeAt(0);
      const isLikelyEmoji =
        charCode > 0x00ff || (charCode >= 0x1f600 && charCode <= 0x1f64f);
      if (isLikelyEmoji) {
        if (currentChunk && !inEmojiChunk) {
          chunks.push({ type: "text", content: currentChunk });
          currentChunk = "";
        }
        inEmojiChunk = true;
        currentChunk += char;
        if (
          nextChar &&
          nextChar.charCodeAt(0) >= 0xfe00 &&
          nextChar.charCodeAt(0) <= 0xfe0f
        ) {
          currentChunk += nextChar;
          i++;
        }
      } else {
        if (currentChunk && inEmojiChunk) {
          chunks.push({ type: "emoji", content: currentChunk });
          currentChunk = "";
        }
        inEmojiChunk = false;
        currentChunk += char;
      }
    }
    if (currentChunk) {
      chunks.push({
        type: inEmojiChunk ? "emoji" : "text",
        content: currentChunk,
      });
    }
    return chunks;
  };

  const renderTextWithEmojis = (pdf, text, x, y, fontSize = 10) => {
    if (!text) return x;
    const fixedText = fixGarbledEmojis(text);
    const chunks = splitTextIntoChunks(fixedText);
    let currentX = x;
    for (const chunk of chunks) {
      if (chunk.type === "text") {
        let run = "";
        let currentFont = "helvetica";
        for (let char of chunk.content) {
          const isEth = isEthiopic(char);
          const newFont = isEth ? "NotoEthiopic" : "helvetica";
          if (newFont !== currentFont) {
            if (run) {
              pdf.setFont(currentFont, "normal");
              pdf.text(run, currentX, y);
              currentX += pdf.getTextWidth(run);
            }
            currentFont = newFont;
            run = char;
          } else {
            run += char;
          }
        }
        if (run) {
          pdf.setFont(currentFont, "normal");
          pdf.text(run, currentX, y);
          currentX += pdf.getTextWidth(run);
        }
      } else if (chunk.type === "emoji") {
        try {
          const emojiImage = createEmojiImage(chunk.content, fontSize);
          if (emojiImage) {
            const emojiSize = fontSize * 0.8;
            pdf.addImage(
              emojiImage,
              "PNG",
              currentX,
              y - fontSize * 0.6,
              emojiSize,
              emojiSize,
            );
            currentX += emojiSize * 0.9;
          } else {
            pdf.setFont("helvetica", "normal");
            pdf.text(chunk.content, currentX, y);
            currentX += pdf.getTextWidth(chunk.content);
          }
        } catch (error) {
          pdf.setFont("helvetica", "normal");
          pdf.text(chunk.content, currentX, y);
          currentX += pdf.getTextWidth(chunk.content);
        }
      }
    }
    return currentX;
  };

  // ---------- Helper: Group notes by task ----------
  const groupedNotes = useMemo(() => {
    const groups = {};
    const groupOrder = [];
    const taskTitleMap = {};

    relatedNotes.forEach((note) => {
      const taskIdKey = note.taskId ?? "no-task";
      if (!groups[taskIdKey]) {
        groups[taskIdKey] = [];
        groupOrder.push(taskIdKey);
        taskTitleMap[taskIdKey] = note.taskTitle || "";
      }
      groups[taskIdKey].push(note);
    });

    for (const taskIdKey in groups) {
      groups[taskIdKey].sort((a, b) => {
        const aTime = new Date(a.timeStamp || a.date);
        const bTime = new Date(b.timeStamp || b.date);
        return aTime - bTime;
      });
    }

    const taskLatestTimestamp = {};
    for (const taskIdKey in groups) {
      let maxTime = null;
      for (const note of groups[taskIdKey]) {
        const time = new Date(note.timeStamp || note.date);
        if (time && !isNaN(time.getTime())) {
          if (maxTime === null || time > maxTime) maxTime = time;
        }
      }
      taskLatestTimestamp[taskIdKey] = maxTime;
    }

    groupOrder.sort((a, b) => {
      const timeA = taskLatestTimestamp[a];
      const timeB = taskLatestTimestamp[b];
      if (timeA === null && timeB === null) return 0;
      if (timeA === null) return -1;
      if (timeB === null) return 1;
      return timeA - timeB;
    });

    return { groups, groupOrder, taskTitleMap };
  }, [relatedNotes]);

  // ---------- Helper functions ----------
  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d)
      ? ""
      : d.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const messageTime = new Date(timestamp);
    if (isNaN(messageTime)) return "";
    const diffInSeconds = Math.floor((now - messageTime) / 1000);
    if (diffInSeconds < 60) return "just now";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 7)
      return `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`;
    return messageTime.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isEthiopic = (char) => {
    const code = char.charCodeAt(0);
    return (
      (code >= 0x1200 && code <= 0x139f) ||
      (code >= 0x2d80 && code <= 0x2ddf) ||
      (code >= 0xab00 && code <= 0xab2f)
    );
  };

// ---------- PDF generation (FIXED) ----------
const generatePDF = async () => {
  let max = maxNotes;
  if (typeof max === "string") {
    const t = max.trim().toLowerCase();
    if (t === "" || t === "all") max = "10000";
  }
  if (isNaN(parseInt(max)) || parseInt(max) <= 0) {
    toast.error("Invalid number of notes.");
    return;
  }

  setPdfGenerating(true);
  try {
    const allNotesInOrder = [];
    for (const taskIdKey of groupedNotes.groupOrder) {
      allNotesInOrder.push(...groupedNotes.groups[taskIdKey]);
    }
    const notesToPrint = allNotesInOrder.slice(-parseInt(max));

    const groupsToPrint = [];
    const tempGroups = {};
    const tempOrder = [];
    const tempTaskTitle = {};
    notesToPrint.forEach((note) => {
      const tid = note.taskId ?? "no-task";
      if (!tempGroups[tid]) {
        tempGroups[tid] = [];
        tempOrder.push(tid);
        tempTaskTitle[tid] = note.taskTitle || "";
      }
      tempGroups[tid].push(note);
    });
    tempOrder.forEach((tid) => {
      groupsToPrint.push({
        taskId: tid,
        notes: tempGroups[tid],
        taskTitle: tempTaskTitle[tid],
      });
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    let currentPage = 1;
    let yPosition = 20;
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    const lineHeight = 5;
    const noteSpacing = 2;
    const maxContentWidth = pageWidth - 2 * margin;
    const footerHeight = 5;

    const addFirstPageHeader = () => {
      const firstNote = relatedNotes[0];
      const wsName = firstNote?.workspace || jobDetails?.workspace || jobDetails?.workspaceName || "";
      const prjName = firstNote?.project || jobDetails?.project || jobDetails?.projectName || "";
      const jbName = firstNote?.job || jobDetails?.name || jobDetails?.jobName || "";
      const taskName = currentTaskTitle || (taskId ? `Task ${taskId}` : "");

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      const title = `Task Notes${taskName ? ` - ${taskName}` : ""}`;
      const titleWidth = pdf.getTextWidth(title);
      pdf.text(title, (pageWidth - titleWidth) / 2, yPosition);
      yPosition += 8;
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      pdf.setFontSize(11);
      pdf.setFont("helvetica", "normal");
      if (wsName) { pdf.text(`Workspace: ${wsName}`, margin, yPosition); yPosition += lineHeight; }
      if (prjName) { pdf.text(`Project: ${prjName}`, margin, yPosition); yPosition += lineHeight; }
      if (jbName) { pdf.text(`Job: ${jbName}`, margin, yPosition); yPosition += lineHeight; }
      if (taskName) { pdf.text(`Task: ${taskName}`, margin, yPosition); yPosition += lineHeight; }
      if (jobDetails?.startDate) { pdf.text(`Start: ${formatDate(jobDetails.startDate)}`, margin, yPosition); yPosition += lineHeight; }
      if (jobDetails?.endDate) { pdf.text(`End: ${formatDate(jobDetails.endDate)}`, margin, yPosition); yPosition += lineHeight; }
      pdf.text(`Printed: ${new Date().toLocaleString()}`, margin, yPosition);
      yPosition += lineHeight + 4;
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;
    };

    addFirstPageHeader();

    // FIXED: Better element rendering with proper sizing
    const addElementToPdf = async (element) => {
      // Create a temporary container with exact dimensions
      const tempContainer = document.createElement("div");
      tempContainer.style.cssText = `
        position: fixed; 
        left: -9999px; 
        top: -9999px; 
        width: ${maxContentWidth}mm; 
        background: white;
        padding: 2mm;
        box-sizing: border-box;
        overflow: visible;
        font-size: 12px;
      `;
      
      // Clone the element to avoid modifying the original
      const clone = element.cloneNode(true);
      // Ensure all images are loaded
      const images = clone.querySelectorAll('img');
      await Promise.all(Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve;
            // If image doesn't load within 5 seconds, continue
            setTimeout(resolve, 5000);
          }
        });
      }));
      
      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);
      
      // Allow time for rendering
      await new Promise((resolve) => setTimeout(resolve, 200));
      
      // Get the actual height of the content
      const rect = tempContainer.getBoundingClientRect();
      const contentHeight = rect.height;
      
      // Calculate if we need a new page
      const availableSpace = pageHeight - margin - footerHeight - yPosition;
      const estimatedHeight = contentHeight + noteSpacing;
      
      if (estimatedHeight > availableSpace && yPosition > margin) {
        pdf.addPage();
        currentPage++;
        yPosition = margin + 1;
      }
      
      // Render with higher quality and proper settings
      const canvas = await html2canvas(tempContainer, {
        scale: 2.5, // Higher scale for better quality
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: tempContainer.scrollWidth,
        height: tempContainer.scrollHeight,
        windowWidth: tempContainer.scrollWidth,
        windowHeight: tempContainer.scrollHeight,
      });
      
      document.body.removeChild(tempContainer);
      
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      
      // Calculate the actual width that fits in the page
      const imgWidth = maxContentWidth - 2; // Slight padding
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add the image to PDF
      pdf.addImage(imgData, "JPEG", margin + 1, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + noteSpacing;
    };

    // FIXED: Build note DOM with proper sizing and overflow handling
    const buildNoteDom = (note, showHeader = true) => {
      const noteContainer = document.createElement("div");
      noteContainer.style.cssText = `
        width: 100%;
        margin: 0 auto;
        padding: 8px;
        background: #f9f9f9;
        border-radius: 4px;
        margin-bottom: ${noteSpacing}mm;
        box-sizing: border-box;
        overflow: visible;
        word-wrap: break-word;
        max-width: 100%;
      `;

      if (showHeader) {
        const noteHeader = document.createElement("div");
        noteHeader.style.cssText = `
          display: flex; 
          justify-content: flex-end; 
          align-items: center; 
          margin-bottom: 4px;
          font-size: 11px;
          color: #666;
          white-space: nowrap;
          overflow: visible;
        `;
        const userDateDiv = document.createElement("div");
        const noteDate = new Date(note.timeStamp || note.date);
        const formattedDate = !isNaN(noteDate.getTime())
          ? noteDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "Invalid Date";
        userDateDiv.textContent = `${note.userName} - ${formattedDate}`;
        noteHeader.appendChild(userDateDiv);
        noteContainer.appendChild(noteHeader);
      }

      const fullNoteDiv = document.createElement("div");
      fullNoteDiv.style.cssText = `
        margin-top: 4px; 
        color: #333; 
        font-size: 12px; 
        line-height: 1.5;
        word-wrap: break-word;
        overflow: visible;
        max-width: 100%;
      `;
      
      let noteHtml = "";
      if (note.siteNoteFriendlyId) {
        noteHtml += `<strong>[${note.siteNoteFriendlyId}]</strong> `;
      }
      noteHtml += note.note || "";
      fullNoteDiv.innerHTML = noteHtml;
      noteContainer.appendChild(fullNoteDiv);

      // Handle inline images with proper loading
      const noteImageIds = note.imageId || [];
      if (noteImageIds.length > 0) {
        const imagesRow = document.createElement("div");
        imagesRow.style.cssText = `
          display: flex; 
          flex-wrap: wrap; 
          gap: 6px; 
          margin-top: 8px;
          max-width: 100%;
        `;
        noteImageIds.forEach((id) => {
          const img = document.createElement("img");
          img.src = `${apiUrl}/InlineImages/GetInlineImage/${id}`;
          img.crossOrigin = "anonymous";
          img.style.cssText = `
            max-width: 150px; 
            max-height: 150px; 
            border-radius: 4px; 
            object-fit: cover;
            flex-shrink: 0;
          `;
          img.loading = "eager";
          imagesRow.appendChild(img);
        });
        noteContainer.appendChild(imagesRow);
      }

      if (note.documentCount > 0) {
        const docCountDiv = document.createElement("div");
        docCountDiv.style.cssText = `
          margin-top: 6px; 
          color: #666; 
          font-size: 10px; 
          display: flex; 
          align-items: center; 
          gap: 4px;
        `;
        docCountDiv.innerHTML = `<i class="fas fa-paperclip"></i> Attachments: ${note.documentCount}`;
        noteContainer.appendChild(docCountDiv);
      }

      const replies = note.replayNotes || [];
      if (replies.length > 0) {
        const repliesContainer = document.createElement("div");
        repliesContainer.style.cssText = `
          margin-top: 10px; 
          margin-left: 15px; 
          border-left: 2px solid #ddd; 
          padding-left: 8px;
          max-width: 100%;
        `;
        replies.forEach((reply) => {
          const replyRow = document.createElement("div");
          replyRow.style.cssText = `
            margin-bottom: 4px; 
            padding: 6px; 
            background: #fff; 
            border-radius: 2px; 
            border: 1px solid #eee;
            overflow: visible;
            max-width: 100%;
          `;
          
          const replyHeader = document.createElement("div");
          replyHeader.style.cssText = `
            display: flex; 
            justify-content: flex-end; 
            align-items: center; 
            margin-bottom: 2px;
            font-size: 9px;
            color: #666;
          `;
          const replyUserDate = document.createElement("div");
          const replyDate = new Date(reply.timeStamp || reply.date);
          const replyFormattedDate = !isNaN(replyDate.getTime())
            ? replyDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Invalid Date";
          replyUserDate.textContent = `${reply.userName} - ${replyFormattedDate}`;
          replyHeader.appendChild(replyUserDate);
          replyRow.appendChild(replyHeader);

          const replyTextDiv = document.createElement("div");
          replyTextDiv.style.cssText = `
            font-size: 11px; 
            color: #333; 
            line-height: 1.4; 
            margin-top: 2px;
            word-wrap: break-word;
            max-width: 100%;
          `;
          let replyText = "↩️ ";
          if (reply.siteNoteFriendlyId) {
            replyText += `<strong>[${reply.siteNoteFriendlyId}]</strong> `;
          }
          replyText += reply.note || "";
          replyTextDiv.innerHTML = replyText;
          replyRow.appendChild(replyTextDiv);

          const replyImageIds = reply.imageId || [];
          if (replyImageIds.length > 0) {
            const replyImagesRow = document.createElement("div");
            replyImagesRow.style.cssText = `
              display: flex; 
              flex-wrap: wrap; 
              gap: 4px; 
              margin-top: 6px;
              max-width: 100%;
            `;
            replyImageIds.forEach((id) => {
              const img = document.createElement("img");
              img.src = `${apiUrl}/InlineImages/GetInlineImage/${id}`;
              img.crossOrigin = "anonymous";
              img.style.cssText = `
                max-width: 120px; 
                max-height: 120px; 
                border-radius: 4px; 
                object-fit: cover;
                flex-shrink: 0;
              `;
              img.loading = "eager";
              replyImagesRow.appendChild(img);
            });
            replyRow.appendChild(replyImagesRow);
          }
          
          if (reply.documentCount && reply.documentCount > 0) {
            const replyDocIndicator = document.createElement("div");
            replyDocIndicator.style.cssText = `
              margin-top: 4px; 
              color: #666; 
              font-size: 9px; 
              display: flex; 
              align-items: center; 
              gap: 4px;
            `;
            replyDocIndicator.innerHTML = `<i class="fas fa-paperclip"></i> Attachments: ${reply.documentCount}`;
            replyRow.appendChild(replyDocIndicator);
          }
          repliesContainer.appendChild(replyRow);
        });
        noteContainer.appendChild(repliesContainer);
      }
      return noteContainer;
    };

    // Process each group
    for (const group of groupsToPrint) {
      const taskDisplay = group.taskTitle
        ? group.taskTitle
        : group.taskId === "no-task"
        ? "No Task"
        : `Task ID: ${group.taskId}`;

      const fullGroupContainer = document.createElement("div");
      fullGroupContainer.style.cssText = `
        width: 100%;
        max-width: ${maxContentWidth}mm;
        overflow: visible;
        padding: 0 2px;
      `;

      const groupHeaderDiv = document.createElement("div");
      groupHeaderDiv.style.cssText = `
        background: #f5f5f5;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 600;
        color: #2c3e50;
        border-bottom: 1px dashed #ddd;
        border-radius: 8px 8px 0 0;
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
        max-width: 100%;
      `;
      groupHeaderDiv.innerHTML = `<i class="fas fa-tasks"></i> ${taskDisplay}`;
      fullGroupContainer.appendChild(groupHeaderDiv);

      // Add all notes in the group
      for (const note of group.notes) {
        const noteDom = buildNoteDom(note, true);
        fullGroupContainer.appendChild(noteDom);
      }

      // Add the group to PDF
      await addElementToPdf(fullGroupContainer);
    }

    // Add page numbers and date to all pages
    const totalPages = currentPage;
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      pdf.setPage(pageNum);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);
      const pageNumberText = `Page ${pageNum} of ${totalPages}`;
      const textWidth = pdf.getTextWidth(pageNumberText);
      const xPosition = (pageWidth - textWidth) / 2;
      pdf.text(pageNumberText, xPosition, pageHeight - 10);
      const currentDate = new Date().toLocaleDateString();
      pdf.text(currentDate, margin, pageHeight - 10);
    }

    const jobName = currentNote?.siteNote?.job || jobDetails?.name || `Job_${jobId || noteId}`;
    const pdfFileName = jobName.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "notes";
    pdf.save(`${pdfFileName}_notes.pdf`);
    toast.success("PDF generated successfully!");
  } catch (error) {
    console.error("Error generating PDF:", error);
    toast.error("Failed to generate PDF: " + error.message);
  } finally {
    setPdfGenerating(false);
    setShowPrintDialog(false);
  }
};
  // ---------- Lightbox ----------
  const openLightboxWithImages = (imageUrls, startIndex = 0) => {
    if (!imageUrls || imageUrls.length === 0) return;
    setCurrentLightboxImages(imageUrls);
    setCurrentLightboxIndex(startIndex);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentLightboxImages([]);
    setCurrentLightboxIndex(0);
    document.body.style.overflow = "auto";
  };

  const navigateLightbox = (direction) => {
    if (!currentLightboxImages.length) return;
    let newIndex = currentLightboxIndex;
    if (direction === "next") {
      newIndex = (currentLightboxIndex + 1) % currentLightboxImages.length;
    } else {
      newIndex =
        (currentLightboxIndex - 1 + currentLightboxImages.length) %
        currentLightboxImages.length;
    }
    setCurrentLightboxIndex(newIndex);
  };

  // ---------- Inline images ----------
  const renderInlineImages = (note) => {
    const ids = note.imageId || [];
    if (ids.length === 0) return null;
    const urls = ids.map((id) => `${apiUrl}/InlineImages/GetInlineImage/${id}`);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
        {urls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt={`image ${i + 1}`}
            style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "6px", cursor: "pointer", objectFit: "cover" }}
            onClick={(e) => { e.stopPropagation(); openLightboxWithImages(urls, i); }}
          />
        ))}
      </div>
    );
  };

  // ---------- Inline image button (UI) — kept for PDF/print path ----------
  const renderImageButton = (note) => {
    const count = note.inlineImageCount || 0;
    if (count === 0) return null;
    return (
      <button
        className="inline-image-button"
        onClick={(e) => {
          e.stopPropagation();
          openLightboxWithImages(note.inlineImageUrls, 0);
        }}
        title={`${count} inline image(s)`}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "0 4px",
          color: "#666",
          fontSize: "14px",
        }}
      >
        <i className="fas fa-image" />
        <span style={{ marginLeft: "2px", fontSize: "12px" }}>{count}</span>
      </button>
    );
  };

  // ---------- Render replies ----------
  const renderNoteReplies = (note) => {
    const replies = note.replayNotes || [];
    if (replies.length === 0) return null;

    const sortedReplies = [...replies].sort((a, b) => {
      const timeA = new Date(a.timeStamp || a.date).getTime();
      const timeB = new Date(b.timeStamp || b.date).getTime();
      return timeA - timeB;
    });

    return (
      <div className="note-replies-container">
        <div className="replies-header">
          <i className="fas fa-reply" /> {sortedReplies.length}{" "}
          {sortedReplies.length === 1 ? "Reply" : "Replies"}
        </div>
        <div className="replies-list">
          {sortedReplies.map((reply) => (
            <div key={reply.id} className="reply-message">
              <div className="reply-content">
                <div className="reply-header">
                  <span className="reply-sender-name">
                    <i
                      className="fas fa-reply fa-flip-horizontal"
                      style={{
                        fontSize: "0.8em",
                        marginRight: "5px",
                        opacity: 0.7,
                      }}
                    />
                    {reply.userName}
                  </span>
                  <span
                    className="reply-time"
                    title={new Date(reply.timeStamp).toLocaleString()}
                  >
                    {formatRelativeTime(reply.timeStamp)}
                  </span>
                </div>
                <div className="reply-text">
                  {reply.siteNoteFriendlyId && (
                    <span
                      className="friendly-id-inline"
                      style={{ fontWeight: "bold", marginRight: "6px" }}
                    >
                      [{reply.siteNoteFriendlyId}]
                    </span>
                  )}
                  <span dangerouslySetInnerHTML={{ __html: reply.note }} />
                </div>
                {renderInlineImages(reply)}
                {(reply.documentCount > 0) && (
                  <div
                    className="reply-attachments"
                    style={{ display: "flex", gap: "6px", marginTop: "6px" }}
                  >
                    {reply.documentCount > 0 && (
                      <button
                        className="reply-attachment-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewAttachments(reply);
                        }}
                        title={`View ${reply.documentCount} attached file(s)`}
                      >
                        <span className="document-count-badge">
                          ({reply.documentCount}){" "}
                        </span>
                        <i className="fas fa-paperclip" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- Fetch manager info ----------
  useEffect(() => {
    const fetchManager = async (id) => {
      if (!id) {
        setManagerInfo(null);
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/UserManagement/GetUserById/${id}`);
        if (!res.ok) throw new Error("Failed to fetch manager");
        const data = await res.json();
        setManagerInfo(data.user || data);
      } catch (err) {
        console.error("Failed to fetch manager info", err);
        setManagerInfo(null);
      }
    };
    if (jobDetails?.managerId) fetchManager(jobDetails.managerId);
  }, [jobDetails]);

  // Scroll to target note if needed
  useEffect(() => {
    if (!loading && relatedNotes.length && (scrollToNoteId || noteId)) {
      const target = scrollToNoteId || noteId;
      const el = noteRefs.current[target];
      if (el && chatContainerRef.current) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.style.transition = "background .4s ease";
          el.style.background = "#e3f2fd";
          setTimeout(() => {
            el.style.background = "";
          }, 2000);
        });
      }
    }
  }, [loading, relatedNotes, scrollToNoteId, noteId]);

  // Lightbox keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;
        case "ArrowLeft":
          navigateLightbox("prev");
          break;
        case "ArrowRight":
          navigateLightbox("next");
          break;
        default:
          break;
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, currentLightboxIndex]);

  if (loading) {
    return (
      <div className={`view-note-modal-overlay theme-${currentTheme}`}>
        <div className="view-note-modal loading">
          <div className="loading-spinner"></div>
          <p>Loading notes...</p>
        </div>
      </div>
    );
  }

  const firstNote = relatedNotes[0];
  const workspaceName = firstNote?.workspace || jobDetails?.workspace || jobDetails?.workspaceName || "";
  const projectName = firstNote?.project || jobDetails?.project || jobDetails?.projectName || "";
  const jobNameDisplay = firstNote?.job || jobDetails?.name || jobDetails?.jobName || "";

  return (
    <>
      <div
        className={`view-note-modal-overlay theme-${currentTheme}`}
        onClick={onClose}
      >
        <div className="view-note-modal" onClick={(e) => e.stopPropagation()}>
          <div className="whatsapp-header">
            <div className="header-left">
              <button className="back-button" onClick={onClose}>
                <i className="fas fa-arrow-left" />
              </button>
              <div className="contact-info">
                <div className="contact-name">
                  Workspace: {workspaceName}
                </div>
                <div className="contact-project">
                  Project: {projectName}
                </div>
                {/* Modified third line: show Job and Task when taskId is present */}
                <div className="contact-status">
                  {taskId ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Job: {jobNameDisplay}</span>
                      <span>Task: {currentTaskTitle || `Task ${taskId}`}</span>
                    </div>
                  ) : (
                    `Job: ${jobNameDisplay}`
                  )}
                </div>
              </div>
            </div>
            <div className="header-right">
              <button
                className="more-info-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowJobInfo((s) => !s);
                }}
                title="More job info"
              >
                <i className="fas fa-info-circle" />
              </button>
              {showJobInfo && jobDetails && (
                <div className="job-attributes">
                  {jobDetails?.type && (
                    <div className="attr">
                      <span className="attr-key">Type:</span>
                      <span className="attr-value">{jobDetails.type}</span>
                    </div>
                  )}
                  {jobDetails?.priorityName &&
                    jobDetails.priorityName !== "Unknown" && (
                      <div className="attr">
                        <span className="attr-key">Priority:</span>
                        <span className="attr-value">
                          {jobDetails.priorityName}
                        </span>
                      </div>
                    )}
                  {jobDetails?.startDate && (
                    <div className="attr">
                      <span className="attr-key">Start:</span>
                      <span className="attr-value">
                        {formatDate(jobDetails.startDate)}
                      </span>
                    </div>
                  )}
                  {jobDetails?.endDate && (
                    <div className="attr">
                      <span className="attr-key">End:</span>
                      <span className="attr-value">
                        {formatDate(jobDetails.endDate)}
                      </span>
                    </div>
                  )}
                  {jobDetails?.actualEndDate && (
                    <div className="attr">
                      <span className="attr-key">Actual End:</span>
                      <span className="attr-value">
                        {formatDate(jobDetails.actualEndDate)}
                      </span>
                    </div>
                  )}
                  {jobDetails?.managerId != null && (
                    <div className="attr manager-info">
                      <span className="attr-key">Manager:</span>
                      <div className="attr-value">
                        <div className="manager-name">
                          {managerInfo
                            ? `${managerInfo.fname || managerInfo.firstName || ""} ${managerInfo.lname || managerInfo.lastName || ""}`.trim()
                            : `ID: ${jobDetails.managerId}`}
                        </div>
                        {managerInfo?.email && (
                          <div className="manager-email">
                            {managerInfo.email}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {jobDetails?.createdDate && (
                    <div className="attr">
                      <span className="attr-key">Created:</span>
                      <span className="attr-value">
                        {formatDate(jobDetails.createdDate)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <button
                className="header-button"
                onClick={() => setShowPrintDialog(true)}
              >
                <i className="fas fa-print" />
              </button>
            </div>
          </div>

          <div className="whatsapp-chat" ref={chatContainerRef}>
            {groupedNotes.groupOrder.map((taskIdKey) => {
              const notesInGroup = groupedNotes.groups[taskIdKey];
              const taskTitle = groupedNotes.taskTitleMap[taskIdKey];
              const taskDisplay = taskTitle
                ? taskTitle
                : taskIdKey === "no-task"
                  ? "No Task"
                  : `Task ID: ${taskIdKey}`;
              return (
                <div key={taskIdKey} className="task-group">
                  <div className="task-group-header">
                    <i className="fas fa-tasks" /> {taskDisplay}
                  </div>
                  {notesInGroup.map((doc) => (
                    <div
                      key={doc.id}
                      ref={(el) => (noteRefs.current[doc.id] = el)}
                      className="message-row"
                      id={`related-note-${doc.id}`}
                    >
                      <div
                        className={`message received ${doc.id === noteId ? "selected" : ""}`}
                        style={{
                          borderLeft:
                            doc.id === noteId ? "4px solid #3498db" : "none",
                          border: "solid 1px #555",
                          background: doc.id === noteId ? "#f0f8ff" : "white",
                        }}
                      >
                        <div className="message-content">
                          <div className="message-header">
                            <span className="sender-name">{doc.userName}</span>
                            <span
                              className="message-time"
                              title={new Date(doc.timeStamp).toLocaleString()}
                            >
                              {formatRelativeTime(doc.timeStamp)}
                            </span>
                          </div>
                          <div className="message-date-below">
                            {formatDate(doc.date)}
                          </div>
                          <div className="message-text">
                            {doc.siteNoteFriendlyId && (
                              <span
                                className="friendly-id-inline"
                                style={{
                                  fontWeight: "bold",
                                  marginRight: "6px",
                                }}
                              >
                                [{doc.siteNoteFriendlyId}]
                              </span>
                            )}
                            <span
                              dangerouslySetInnerHTML={{ __html: doc.note }}
                            />
                          </div>
                          {renderInlineImages(doc)}
                          {renderNoteReplies(doc)}
                        </div>
                      </div>

                      <div
                        className="paperclip-container"
                        style={{
                          display: "flex",
                          gap: "4px",
                          alignItems: "center",
                        }}
                      >
                        {doc.documentCount > 0 && (
                          <button
                            className="paperclip-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewAttachments(doc);
                            }}
                            title={`View ${doc.documentCount} attached file(s)`}
                          >
                            <span className="document-count-badge">
                              ({doc.documentCount}){" "}
                            </span>
                            <i className="fas fa-paperclip" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="whatsapp-footer">
            <div className="footer-date">
              <i className="fas fa-calendar-alt" />{" "}
              {new Date().toLocaleDateString()}
            </div>
            <button className="close-chat-button" onClick={onClose}>
              <i className="fas fa-times" /> Close
            </button>
          </div>
        </div>
      </div>

      {showPrintDialog && (
        <div
          className="print-dialog-overlay"
          onClick={() => setShowPrintDialog(false)}
        >
          <div className="print-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Print Notes to PDF</h3>
            {pdfGenerating ? (
              <div className="pdf-generating">
                <i className="fas fa-spinner fa-spin" /> Generating PDF...
              </div>
            ) : (
              <>
                <div className="print-options">
                  <div className="print-option-group">
                    <label>
                      Maximum number of recent notes:
                      <input
                        type="text"
                        value={maxNotes}
                        onChange={(e) => setMaxNotes(e.target.value)}
                        placeholder="All"
                        className="notes-input"
                      />
                    </label>
                  </div>
                </div>
                <div className="buttons">
                  <button className="generate-btn" onClick={generatePDF}>
                    <i className="fas fa-file-pdf" /> Generate PDF
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowPrintDialog(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {lightboxOpen && currentLightboxImages.length > 0 && (
        <div className="fullscreen-lightbox">
          <div className="lightbox-background" onClick={closeLightbox}></div>
          <div className="lightbox-image-wrapper" ref={lightboxRef}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times" />
            </button>
            <div className="lightbox-counter">
              {currentLightboxIndex + 1} / {currentLightboxImages.length}
            </div>
            <img
              src={currentLightboxImages[currentLightboxIndex]}
              alt="Full size inline"
              className="lightbox-full-image"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/800x600?text=Image+Not+Found";
              }}
            />
            {currentLightboxImages.length > 1 && (
              <>
                <button
                  className="lightbox-nav lightbox-prev"
                  onClick={() => navigateLightbox("prev")}
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <button
                  className="lightbox-nav lightbox-next"
                  onClick={() => navigateLightbox("next")}
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

ViewNoteModal.propTypes = {
  noteId: PropTypes.number,
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taskId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  currentTheme: PropTypes.string,
  onViewAttachments: PropTypes.func,
  userid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scrollToNoteId: PropTypes.number,
};

ViewNoteModal.defaultProps = {
  noteId: null,
  jobId: null,
  taskId: null,
  currentTheme: "light",
  onViewAttachments: () => {},
  userid: null,
  scrollToNoteId: null,
};

export default ViewNoteModal;